import {
  Box,
  List,
  ListItem,
  Stack,
  Typography,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import React, { useMemo, type ReactNode } from "react";
import { useSelector } from "react-redux";

import Flag from "../../common/flag";

import type { RootState } from "../../../store/reducers/reducer";

interface SublistItem {
  companyEmail?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  company_id?: string | null;
  company_name?: string | null;
  modifiedDt?: string | null;
  personEmail?: string | null;
  personId?: string | null;
  personName?: string | null;
  person_id?: string | null;
  person_name?: string | null;
  recipientId?: string | null;
  type?: string | null;
}

interface NotificationListProps {
  data: SublistItem[] | null | undefined;
  mainIcon?: ReactNode;
  useNumbering?: boolean;
  showListBorder?: boolean; // Optional border for the container
  containerSx?: any; // Custom styling for container
}

// helpers to detect type-like objects (accept snake_case or camelCase)
const looksLikeCompany = (obj: any) =>
  !!obj && (obj.type === "COMPANY" || obj.company_id || obj.companyId || obj.company_name || obj.companyName);

const looksLikePerson = (obj: any) =>
  !!obj && (obj.type === "PERSON" || obj.person_id || obj.personId || obj.person_name || obj.personName);

const NotificationList: React.FC<NotificationListProps> = ({
  data = [],
  mainIcon = "•",
  useNumbering = false,
  showListBorder = true,
  containerSx = {},
}) => {
  const { peopleList } = useSelector((state: RootState) => state.peopleList);
  const { companyList } = useSelector((state: RootState) => state.companyList);

  const companyObjList = useMemo(
    () =>
      (companyList || []).reduce((acc: Record<string, any>, company: any) => {
        if (company?.companyId) acc[company.companyId] = company;
        if (company?.company_id) acc[company.company_id] = company;
        return acc;
      }, {}),
    [companyList]
  );

  const peopleById = useMemo(() => {
    const map: Record<string, any> = {};
    (peopleList || []).forEach((person: any) => {
      if (person?.personId) map[person.personId] = person;
      if (person?.person_id) map[person.person_id] = person;
    });
    return map;
  }, [peopleList]);

  // normalize incoming data to array of items that can be displayed
  const normalized = useMemo(() => {
    const arr = (data || []).filter((d) => d != null);
    // sort by person_name or company_name (accept both key styles)
    arr.sort((a: any, b: any) => {
      const nameA = String(a?.person_name || a?.personName || a?.company_name || a?.companyName || "");
      const nameB = String(b?.person_name || b?.personName || b?.company_name || b?.companyName || "");
      return nameA.localeCompare(nameB);
    });

    // convert to a display-friendly structure and RETURN it
    return arr.map((item: any) => {
      const isCompany = looksLikeCompany(item);
      const isPerson = looksLikePerson(item);
      
      const id = item?.person_id || item?.personId || item?.company_id || item?.companyId || item?.recipientId || undefined;
      const name = item?.person_name || item?.personName || item?.company_name || item?.companyName || item?.value || "";
      
      const companyKey = item?.company_id || item?.companyId;
      const personKey = item?.person_id || item?.personId;

      // Get flag data for both companies and people
      let flag = false;
      let flagComments = "";
      let textTooltip = "";

      if (isCompany && companyKey) {
        const companyData = companyObjList[companyKey];
        flag = companyObjList[companyKey]?.flag || true;
        flagComments = companyData?.flagComments || "";
        textTooltip = "Company";
      } else if (isPerson && personKey) {
        const personData = peopleById[personKey];
        flag = !!personData?.flag;
        flagComments = personData?.flagComments || "";
        textTooltip = "Person";
      }

      return {
        raw: item,
        id,
        name,
        isCompany,
        isPerson,
        flag,
        flagComments,
        textTooltip,
      } as const;
    });
  }, [data, companyObjList, peopleById]);

  // If no data, return empty state
  if (!normalized.length) {
    return (
      <Box 
        sx={{ 
          p: 1, 
          minWidth: 240, 
          ...(showListBorder && { border: "1px solid", borderColor: "divider" }),
          ...containerSx 
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No notifications
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        px: 1, 
        minWidth: 240, 
        ...(showListBorder && { border: "1px solid", borderColor: "divider" }),
        ...containerSx 
      }}
    >
      <Stack spacing={1}>
        <List dense>
          {normalized.map((item, idx) => (
            <ListItem key={item.id ?? `item-${idx}`} sx={{ py: 0.5, px: 0 }}>
              {/* Show numbering or bullet point */}
              <ListItemIcon sx={{ minWidth: 30 }}>
                {useNumbering ? `${idx + 1}.` : mainIcon}
              </ListItemIcon>

              {/* Use Flag component for both companies and people */}
              <ListItemText 
                primary={
                  <Flag 
                    flag={item.flag}
                    flagTooltip={item.flagComments}
                    textTooltip={item.textTooltip}
                    name={item.name}
                    variant="body2"
                  />
                } 
              />
            </ListItem>
          ))}
        </List>
      </Stack>
    </Box>
  );
};

export default NotificationList;